import React, { useState } from 'react';
import './UserMenu.css';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { usePremiumUpgrade } from './hooks/usePremiumUpgrade';
import CheckoutErrorNotice from './components/CheckoutErrorNotice/CheckoutErrorNotice';

const UserMenu = ({ variant = 'default', onNavigate }) => {
  const { user, isLoading, logout } = useAuth();
  const [showDropdown, setShowDropdown] = useState(false);
  const navigate = useNavigate();
  const isSheet = variant === 'sheet';
  const { startCheckout, checkoutError, isStartingCheckout } = usePremiumUpgrade({
    returnPath: window.location.pathname,
    source: 'user_menu',
    placement: 'user_menu',
  });

  const closeMenu = () => {
    setShowDropdown(false);
    if (onNavigate) onNavigate();
  };

  const isPremiumActive = (() => {
    if (!user) return false;
    if (user.is_premium_active === true) return true;
    if (user.access_level !== 'premium') return false;
    if (!user.premium_expires_at) return true;
    const expiresAt = new Date(user.premium_expires_at);
    if (Number.isNaN(expiresAt.getTime())) return false;
    return expiresAt.getTime() > Date.now();
  })();

  const accessClass = isPremiumActive ? 'premium' : 'free';
  const accessLabel = isPremiumActive
    ? 'Premium'
    : (user?.access_level === 'premium' ? 'Premium Udløb' : 'Basis');

  const handleLogin = () => {
    navigate('/login');
    closeMenu();
  };

  const handleLogout = async () => {
    await logout();
    closeMenu();
  };

  const toggleDropdown = () => {
    setShowDropdown(!showDropdown);
  };

  const menuClassName = [
    'user-menu',
    user ? 'authenticated' : '',
    isSheet ? 'user-menu--sheet' : '',
    isLoading ? 'loading' : '',
  ]
    .filter(Boolean)
    .join(' ');

  if (isLoading) {
    return (
      <div className={menuClassName}>
        <span className="loading-text">Indlæser konto...</span>
      </div>
    );
  }

  if (!user) {
    return (
      <div className={menuClassName}>
        {isSheet && <p className="user-menu-sheet-heading">Konto</p>}
        <button
          type="button"
          className={isSheet ? 'user-menu-sheet-action user-menu-sheet-action--primary' : 'login-btn'}
          onClick={handleLogin}
        >
          Log ind
        </button>
      </div>
    );
  }

  return (
    <div className={menuClassName}>
      {isSheet && <p className="user-menu-sheet-heading">Konto</p>}
      <button
        type="button"
        className={`user-info ${isSheet ? 'user-info--sheet' : ''}`}
        onClick={toggleDropdown}
        aria-expanded={showDropdown}
        aria-haspopup="true"
      >
        <span className="user-greeting">Hej, {user.username}</span>
        <span className={`access-badge ${accessClass}`}>
          {accessLabel}
        </span>
        <span className={`dropdown-arrow ${showDropdown ? 'open' : ''}`} aria-hidden="true">
          ▾
        </span>
      </button>

      {showDropdown && (
        <div className={`user-dropdown ${isSheet ? 'user-dropdown--sheet' : ''}`}>
          <div className="dropdown-header">
            <div className="user-details">
              <strong>{user.username}</strong>
              <span className="user-email">{user.email}</span>
              <span className={`access-status ${accessClass}`}>
                {isPremiumActive ? 'Premium konto' : (user.access_level === 'premium' ? 'Premium Udløb' : 'Basis konto')}
              </span>
            </div>
          </div>

          <div className="dropdown-actions">
            <button
              type="button"
              className="profile-btn"
              onClick={() => {
                navigate('/profile');
                closeMenu();
              }}
            >
              Min Profil
            </button>
            {!isPremiumActive && (
              <>
                <CheckoutErrorNotice message={checkoutError} onRetry={startCheckout} />
                <button
                  type="button"
                  className="upgrade-btn"
                  onClick={async () => {
                    const result = await startCheckout();
                    if (result?.ok) {
                      closeMenu();
                    }
                  }}
                  disabled={isStartingCheckout}
                >
                  {isStartingCheckout ? 'Åbner betaling...' : 'Opgrader til Premium'}
                </button>
              </>
            )}
            <button type="button" className="logout-btn" onClick={handleLogout}>
              Log ud
            </button>
          </div>
        </div>
      )}

      {showDropdown && !isSheet && (
        <div
          className="dropdown-overlay"
          onClick={() => setShowDropdown(false)}
          aria-hidden="true"
        />
      )}
    </div>
  );
};

export default UserMenu;
